/**
 * Web app only (card 2.3): the refresh token lives in an HttpOnly cookie on
 * api.apiboo.com (backend WebSessionController), never in localStorage where
 * any script on the page could read it. The browser keeps only the
 * short-lived access token.
 *
 *   adoptRefreshToken(rt) — right after sign-in: the server rotates rt at the
 *                           IdP, sets the cookie, returns an access token
 *   refreshWebSession()   — the cookie is sent automatically; new access token
 *   endWebSession()       — revoke at the IdP and clear the cookie
 *
 * Error shapes match the desktop refresh path, so the interceptors keep their
 * rules: an HTTP rejection carries `response` (session gone), a transport
 * failure or an unreachable IdP (503) is `code: 'ERR_NETWORK'` (keep the session).
 */
import { expiresAtFromExpiresIn } from '../utils/oauthPkce';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1').replace(/\/+$/, '');
/**
 * Non-secret marker: "this browser has a cookie session".
 *
 * NOT under the `apiboo.` prefix on purpose — that family is device-global
 * (theme, window size) and is deliberately spared by `clearAccountNamespace`.
 * A session marker that survives a sign-out lets the very next request refresh
 * itself back into a signed-in state off the cookie.
 */
export const WEB_SESSION_FLAG = 'webSession.active';

function networkError(message) {
  const err = new Error(message || 'Network error');
  err.isAxiosError = true;
  err.code = 'ERR_NETWORK';
  return err;
}

async function post(path, body) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', accept: 'application/json', 'x-apiboo-web-session': '1' },
      body: body ? JSON.stringify(body) : '{}',
    });
  } catch (e) {
    throw networkError(e?.message);
  }
  if (res.status === 204) return null;
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  // Anything that is not the server saying "this credential is no good" is an
  // outage: a 403 is a misconfigured origin, a 5xx is a deploy in flight.
  // Treating those as a dead session signs every open tab out for good.
  if (res.status === 503 || res.status >= 500 || res.status === 403) {
    throw networkError(`web_session_${res.status}`);
  }
  if (!res.ok) {
    const err = new Error(`Web session request failed (${res.status})`);
    err.isAxiosError = true;
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

function markSession(on) {
  try {
    if (on) localStorage.setItem(WEB_SESSION_FLAG, '1');
    else localStorage.removeItem(WEB_SESSION_FLAG);
  } catch {
    /* storage unavailable */
  }
}

export function hasWebSession() {
  try {
    return localStorage.getItem(WEB_SESSION_FLAG) === '1';
  } catch {
    return false;
  }
}

function toTokens(data) {
  if (!data?.access_token) throw new Error('Sign-in failed: no access token was returned.');
  return { accessToken: data.access_token, refreshToken: null, expiresAt: expiresAtFromExpiresIn(data.expires_in) };
}

export async function adoptRefreshToken(refreshToken) {
  const tokens = toTokens(await post('/web-session', { refresh_token: refreshToken }));
  markSession(true);
  return tokens;
}

export async function refreshWebSession() {
  try {
    return toTokens(await post('/web-session/refresh'));
  } catch (err) {
    if (err?.response?.status === 401) markSession(false); // the cookie session is over
    throw err;
  }
}

/**
 * Ends the cookie session. Returns true when the server confirmed it.
 *
 * The marker is cleared only after the server answered: clearing it first and
 * swallowing a failure leaves a live 30-day refresh cookie behind a UI that
 * says "signed out" — on a shared machine that is someone else's session.
 */
export async function endWebSession() {
  try {
    await post('/web-session/logout');
    markSession(false);
    return true;
  } catch (e) {
    // A 401 means it was already gone, which is the outcome we wanted.
    if (e?.response?.status === 401) {
      markSession(false);
      return true;
    }
    console.warn('Web session sign-out did not reach the server:', e?.message || e);
    return false;
  }
}

/** Forget the local marker without calling the server (a forced sign-out). */
export function forgetWebSession() {
  markSession(false);
}
